import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useAuth } from '../contexts/AuthContext';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { ApiError, classesApi, groupsApi } from '../services/api/client';

type Target =
  | { kind: 'turma'; name: string; detail: string; professorName: string }
  | { kind: 'grupo'; name: string; detail: string; classId: string };

/**
 * Tela de entrada por QR Code / link, para turma e para grupo.
 *
 * Ela mostra o destino ANTES de confirmar, em vez de matricular no
 * carregamento. Quem escaneia um QR precisa ver onde está entrando.
 *
 * O convite de TURMA agora é o único caminho de criação de conta (PRD
 * 33.1) — por isso o peek dele é público (funciona sem login) e, para quem
 * ainda não tem conta, esta tela oferece "criar conta" além de "entrar". O
 * convite de GRUPO nunca cria conta (só matricula quem já é da turma), então
 * continua exigindo login antes de qualquer coisa.
 */
export default function JoinPage() {
  const { kind = '', token = '' } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [target, setTarget] = useState<Target | null>(null);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [done, setDone] = useState<{ message: string; classId: string } | null>(null);

  const isValidKind = kind === 'turma' || kind === 'grupo';
  const destination = `/entrar/${kind}/${token}`;

  useEffect(() => {
    if (!isValidKind) return;
    // Grupo nunca cria conta — sem sentido espiar antes de saber quem é.
    if (kind === 'grupo' && !user) return;

    const request =
      kind === 'turma'
        ? classesApi.peekInvite(token).then(
            (result): Target => ({
              kind: 'turma',
              name: result.class.name,
              detail: `${result.class.discipline} · ${result.class.course} · ${result.class.academicTerm}`,
              professorName: result.class.professorName,
            }),
          )
        : groupsApi.peekInvite(token).then(
            (result): Target => ({
              kind: 'grupo',
              name: result.group.name,
              detail: `${result.group.className} · ${result.group.discipline}`,
              classId: result.group.classId,
            }),
          );

    request
      .then(setTarget)
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : 'Não foi possível ler este convite.',
        ),
      );
  }, [isValidKind, kind, token, user]);

  if (loading) {
    return (
      <div className="centered-state">
        <p>Carregando…</p>
      </div>
    );
  }

  if (!isValidKind) {
    return <Navigate to="/" replace />;
  }

  // Grupo sem sessão: continua indo direto para o login, como antes.
  if (!user && kind === 'grupo') {
    return <Navigate to="/login" replace state={{ from: destination }} />;
  }

  async function handleJoin() {
    setJoining(true);
    setError('');

    try {
      if (kind === 'turma') {
        const result = await classesApi.acceptInvite(token);
        setDone({
          message: result.alreadyMember
            ? 'Você já estava nesta turma.'
            : 'Pronto! Você entrou na turma.',
          classId: result.classId,
        });
      } else {
        const result = await groupsApi.acceptInvite(token);
        setDone({
          message: result.alreadyMember
            ? 'Você já estava neste grupo.'
            : 'Pronto! Você entrou no grupo.',
          classId: result.classId,
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar.');
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Convite</p>

        {done ? (
          <>
            <h1>{done.message}</h1>
            <button
              type="button"
              className="primary"
              onClick={() => void navigate(`/turmas/${done.classId}`, { replace: true })}
            >
              Ir para a turma
            </button>
          </>
        ) : (
          <>
            <h1>Entrar {kind === 'turma' ? 'na turma' : 'no grupo'}</h1>

            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}

            {!target && !error && <p className="muted">Verificando o convite…</p>}

            {target && (
              <>
                <div className="invite-target">
                  <strong>{target.name}</strong>
                  <p className="muted small">{target.detail}</p>
                  {target.kind === 'turma' && (
                    <p className="muted small">Prof. {target.professorName}</p>
                  )}
                </div>

                {user ? (
                  <button
                    type="button"
                    className="primary"
                    onClick={() => void handleJoin()}
                    disabled={joining}
                  >
                    {joining ? 'Entrando…' : 'Confirmar entrada'}
                  </button>
                ) : (
                  // Sem sessão, só chega aqui em kind === 'turma' (grupo já
                  // redirecionou acima). Cria conta OU entra com uma
                  // existente — as duas já sabem para onde voltar depois.
                  <div className="button-row">
                    <button
                      type="button"
                      className="primary"
                      onClick={() =>
                        void navigate('/cadastro', { state: { token, from: destination } })
                      }
                    >
                      Criar conta
                    </button>
                    <button
                      type="button"
                      onClick={() => void navigate('/login', { state: { from: destination } })}
                    >
                      Já tenho conta
                    </button>
                  </div>
                )}

                {!user && <SocialLoginButtons inviteToken={token} />}
              </>
            )}

            <p className="auth-switch">
              <button type="button" className="as-link" onClick={() => void navigate('/')}>
                Cancelar
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
